import { Autocomplete, Checkbox, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { FilterableSelectOption } from './FilterableSelect';
import { matchesFilterableOption } from './filterMatch';

interface FilterableMultiSelectProps {
  label: string;
  options: FilterableSelectOption[];
  /** The selected values — order preserved as picked/typed. */
  values: string[];
  onChange: (values: string[]) => void;
  helperText?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Accepts typed values not present in the option list (Enter adds them as chips) — for filters whose population may be incomplete. */
  freeSolo?: boolean;
}

/**
 * `FilterableSelect`'s multi-value sibling: the same type-to-filter matching
 * over `{ value, label, secondary, keywords }` options, but every pick toggles
 * a checkbox and lands as a removable chip — the multi-value semantics of the
 * repeatable query parameters this backs are preserved, never collapsed to a
 * single value.
 */
export function FilterableMultiSelect({
  label,
  options,
  values,
  onChange,
  helperText,
  disabled,
  loading,
  freeSolo,
}: FilterableMultiSelectProps) {
  const translate = useTranslate();
  // A value picked earlier (or typed, under freeSolo) that is not in the
  // current option list still renders as its own chip rather than vanishing.
  const selected = values.map(
    (value) => options.find((option) => option.value === value) ?? { value, label: value },
  );

  return (
    <Autocomplete
      multiple
      size="small"
      fullWidth
      disableCloseOnSelect
      options={options}
      value={selected}
      freeSolo={freeSolo}
      loading={loading}
      disabled={disabled}
      onChange={(_event, picked) =>
        onChange(picked.map((option) => (typeof option === 'string' ? option : option.value)))
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, current) => option.value === (typeof current === 'string' ? current : current.value)}
      filterOptions={(candidates, state) => {
        const input = state.inputValue.trim().toLowerCase();
        return input ? candidates.filter((candidate) => matchesFilterableOption(candidate, input)) : candidates;
      }}
      noOptionsText={translate('pay.filterableSelect.noOptions')}
      loadingText={translate('pay.filterableSelect.loading')}
      renderOption={({ key, ...optionProps }, option, { selected: isSelected }) => (
        <li key={key} {...optionProps}>
          <Checkbox size="small" checked={isSelected} sx={{ mr: 1, p: 0.5 }} />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2">{option.label}</Typography>
            {option.secondary && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.secondary}
              </Typography>
            )}
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
